import { assertCompanyWriteAccess, assertPersonAdminFieldWriteAccess, assertPersonWriteAccess, assertProjectCreateAccess, assertProjectGroupWriteAccess, assertProjectWriteAccess, normalizeProjectInput, queueClockifyProjectGroupSync, queueClockifyProjectSync, } from "../projects-helpers.js";
import { companyIDInputSchema, createCompanyInputSchema, createPersonInputSchema, createProjectGroupInputSchema, createProjectInputSchema, personIDInputSchema, projectGroupIDInputSchema, projectIDInputSchema, updateCompanyInputSchema, updatePersonInputSchema, updateProjectGroupInputSchema, updateProjectInputSchema, } from "../projects-input-schemas.js";
function authorizedProjectRowsQuery(projectZql, ctx) {
    const personID = ctx.personID;
    return ctx.role === "admin" || ctx.role === "employee"
        ? projectZql.projects
        : ctx.role === "freelancer" && personID
            ? projectZql.projects.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID))
            : projectZql.projects.where("id", "__unauthorized__");
}
function authorizedProjectsQuery(projectZql, ctx) {
    return authorizedProjectRowsQuery(projectZql, ctx)
        .related("companies")
        .related("contact")
        .related("project_manager")
        .related("account_manager")
        .related("technical_responsible")
        .related("equipment_responsible")
        .related("project_groups");
}
function authorizedCompaniesQuery(projectZql, ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.companies.where("id", "__unauthorized__");
    }
    return projectZql.companies;
}
function withCompanyDeletedScope(query, includeDeleted) {
    return includeDeleted ? query : query.where("deletedAt", "IS", null);
}
function authorizedProjectGroupsQuery(projectZql, ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.projectGroups.where("id", "__unauthorized__");
    }
    return projectZql.projectGroups;
}
function projectGroupsWithRelations(projectZql, ctx) {
    return authorizedProjectGroupsQuery(projectZql, ctx)
        .related("companies")
        .related("contact")
        .related("projects");
}
function withProjectGroupDeletedScope(query, includeDeleted) {
    return includeDeleted ? query : query.where("deletedAt", "IS", null);
}
function authorizedPeopleQuery(projectZql, ctx) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.people;
    }
    return ctx.userID
        ? projectZql.people.where("userID", ctx.userID)
        : projectZql.people.where("id", "__unauthorized__");
}
function peopleWithCompany(projectZql, ctx) {
    return authorizedPeopleQuery(projectZql, ctx)
        .related("companies")
        .related("personAllergyTags", (assignment) => assignment.related("allergyTag"));
}
function withPeopleDeletedScope(query, includeDeleted) {
    return includeDeleted ? query : query.where("deletedAt", "IS", null);
}
export function createCoreQueries({ defineProjectQuery, projectZql, }) {
    if (!defineProjectQuery) {
        throw new Error("Missing core query dependencies");
    }
    return {
        projects: {
            commandItems: defineProjectQuery(({ ctx }) => authorizedProjectRowsQuery(projectZql, ctx)
                .where("deletedAt", "IS", null)
                .orderBy("eventStartDate", "desc")
                .orderBy("createdAt", "desc")),
            list: defineProjectQuery(({ ctx }) => authorizedProjectsQuery(projectZql, ctx)
                .orderBy("eventStartDate", "desc")
                .orderBy("createdAt", "desc")),
            myUpcoming: defineProjectQuery(({ args, ctx, }) => {
                if (!ctx.personID || ctx.personID !== args.personID) {
                    return projectZql.projectAvailability.where("id", "__unauthorized__");
                }
                return projectZql.projectAvailability
                    .where("personID", args.personID)
                    .where("status", "IN", ["requested", "available", "booked"])
                    .related("project", (project) => project
                    .where("deletedAt", "IS", null)
                    .related("companies")
                    .related("contact")
                    .related("project_manager")
                    .related("account_manager")
                    .related("technical_responsible")
                    .related("equipment_responsible")
                    .related("project_groups"))
                    .orderBy("createdAt", "asc");
            }),
        },
        companies: {
            list: defineProjectQuery(({ args, ctx, }) => withCompanyDeletedScope(authorizedCompaniesQuery(projectZql, ctx), args.includeDeleted).orderBy("name", "asc")),
            byID: defineProjectQuery(({ args, ctx }) => authorizedCompaniesQuery(projectZql, ctx).where("id", args.id).one()),
        },
        projectGroups: {
            commandItems: defineProjectQuery(({ ctx }) => withProjectGroupDeletedScope(authorizedProjectGroupsQuery(projectZql, ctx), false).orderBy("createdAt", "desc")),
            list: defineProjectQuery(({ args, ctx, }) => withProjectGroupDeletedScope(projectGroupsWithRelations(projectZql, ctx), args.includeDeleted).orderBy("createdAt", "desc")),
            byID: defineProjectQuery(({ args, ctx }) => projectGroupsWithRelations(projectZql, ctx)
                .where("id", args.id)
                .one()),
        },
        people: {
            list: defineProjectQuery(({ args, ctx, }) => {
                let query = withPeopleDeletedScope(peopleWithCompany(projectZql, ctx), args.includeDeleted);
                if (args.personTypes?.length === 1) {
                    query = query.where("personType", args.personTypes[0]);
                }
                else if (args.personTypes && args.personTypes.length > 1) {
                    query = query.where("personType", "IN", args.personTypes);
                }
                return query.orderBy("firstName", "asc").orderBy("lastName", "asc");
            }),
            byID: defineProjectQuery(({ args, ctx }) => peopleWithCompany(projectZql, ctx).where("id", args.id).one()),
            byUserID: defineProjectQuery(({ args, ctx }) => {
                if (ctx.userID !== args.userID) {
                    return projectZql.people.where("id", "__unauthorized__").one();
                }
                return projectZql.people
                    .where("userID", args.userID)
                    .where("deletedAt", "IS", null)
                    .one();
            }),
        },
    };
}
export function createCoreMutators({ defineProjectMutator }) {
    if (!defineProjectMutator) {
        throw new Error("Missing core mutator dependencies");
    }
    return {
        projects: {
            create: defineProjectMutator(createProjectInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectCreateAccess(ctx);
                await tx.mutate.projects.insert({
                    ...normalizeProjectInput(args),
                    status: args.status ?? "initial_contact",
                    types: args.types ?? [],
                    language: args.language ?? "de",
                    defaultVatRate: args.defaultVatRate ?? "CH_8_1",
                    defaultDiscountPercent: args.defaultDiscountPercent ?? 0,
                });
                queueClockifyProjectSync(ctx, args.id);
                if (args.projectGroupID) {
                    queueClockifyProjectGroupSync(ctx, args.projectGroupID);
                }
            }),
            update: defineProjectMutator(updateProjectInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.id);
                await tx.mutate.projects.update(normalizeProjectInput(args));
                queueClockifyProjectSync(ctx, args.id);
                if (args.projectGroupID) {
                    queueClockifyProjectGroupSync(ctx, args.projectGroupID);
                }
            }),
            softDelete: defineProjectMutator(projectIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.id);
                await tx.mutate.projects.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            restore: defineProjectMutator(projectIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.id);
                await tx.mutate.projects.update({
                    id: args.id,
                    deletedAt: null,
                });
            }),
        },
        companies: {
            create: defineProjectMutator(createCompanyInputSchema, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.insert({
                    ...args,
                    types: args.types ?? [],
                    isMainCompany: args.isMainCompany ?? false,
                });
            }),
            update: defineProjectMutator(updateCompanyInputSchema, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.update(args);
            }),
            softDelete: defineProjectMutator(companyIDInputSchema, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            restore: defineProjectMutator(companyIDInputSchema, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.update({
                    id: args.id,
                    deletedAt: null,
                });
            }),
        },
        projectGroups: {
            create: defineProjectMutator(createProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.insert({
                    ...args,
                    status: args.status ?? "active",
                });
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
            update: defineProjectMutator(updateProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.update(args);
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
            softDelete: defineProjectMutator(projectGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
            restore: defineProjectMutator(projectGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.update({
                    id: args.id,
                    deletedAt: null,
                });
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
        },
        people: {
            create: defineProjectMutator(createPersonInputSchema, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                if (args.isAdmin !== undefined) {
                    assertPersonAdminFieldWriteAccess(ctx);
                }
                await tx.mutate.people.insert({
                    ...args,
                    personType: args.personType ?? "contact",
                    isAdmin: args.isAdmin ?? false,
                });
            }),
            update: defineProjectMutator(updatePersonInputSchema, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                if (args.isAdmin !== undefined) {
                    assertPersonAdminFieldWriteAccess(ctx);
                }
                await tx.mutate.people.update(args);
            }),
            softDelete: defineProjectMutator(personIDInputSchema, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                await tx.mutate.people.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            restore: defineProjectMutator(personIDInputSchema, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                await tx.mutate.people.update({
                    id: args.id,
                    deletedAt: null,
                });
            }),
        },
    };
}
